/**
 * Formats minor units (pence) to a localised GBP string.
 *
 * GBP in every locale; Latin (Western) digits always, for B2B price + invoice
 * clarity. Pass the active locale so grouping/format follow the user's locale
 * but the currency + digits stay fixed. Defaults to "en-GB" so existing call
 * sites that omit locale compile and behave identically.
 *
 * Examples (en-GB):
 *   formatGBP(850)  → "£8.50"
 *   formatGBP(0)    → "£0.00"
 *   formatGBP(2550) → "£25.50"
 */
export function formatGBP(minor: number, locale: string = "en-GB"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "GBP",
    numberingSystem: "latn",
  }).format(minor / 100);
}

/**
 * Formats two minor-unit amounts as "ex-VAT (+ VAT) inc-VAT" copy used on
 * cart line rows and totals blocks. Returns a tuple-shaped string so
 * Tailwind can size the parts independently if needed.
 *
 * Examples (en-GB):
 *   formatGBPInclVAT(850, 0)    → { ex: "£8.50", vat: "£0.00", inc: "£8.50" }
 *   formatGBPInclVAT(425, 85)   → { ex: "£4.25", vat: "£0.85", inc: "£5.10" }
 */
export function formatGBPInclVAT(
  exVatMinor: number,
  vatMinor: number,
  locale: string = "en-GB",
): { ex: string; vat: string; inc: string } {
  return {
    ex: formatGBP(exVatMinor, locale),
    vat: formatGBP(vatMinor, locale),
    inc: formatGBP(exVatMinor + vatMinor, locale),
  };
}

/**
 * Formats an ISO date string for display. Month names follow the active locale
 * but Latin (Western) digits are enforced for B2B/invoice readability across
 * all locales. Defaults to "en-GB" so existing call sites that omit locale
 * compile and behave identically.
 *
 * Examples (en-GB):
 *   formatDate("2026-05-13T00:00:00.000Z") → "13 May 2026"
 *   formatDate("2026-01-01T00:00:00.000Z") → "01 Jan 2026"
 */
export function formatDate(iso: string, locale: string = "en-GB"): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    numberingSystem: "latn",
  }).format(new Date(iso));
}
